import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errMessage } from "@/lib/api";
import { ENQUIRY_TOPICS } from "@/constants/site";

const EMPTY = { name: "", telegram: "", email: "", topic: "general", message: "" };

export const EnquiryModal = ({ open, onOpenChange, config, defaultTopic = "general" }) => {
  const [form, setForm] = useState({ ...EMPTY, topic: defaultTopic });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Put your name lah, at least 2 letters.";
    if (!form.telegram.trim() && !form.email.trim())
      next.telegram = "Give us one contact - Telegram handle or email.";
    if (form.message.trim().length < 5) next.message = "Tell us a bit more, minimum 5 characters.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.createLead({
        name: form.name.trim(),
        telegram: form.telegram.trim() || null,
        email: form.email.trim() || null,
        topic: form.topic,
        message: form.message.trim(),
      });
      setDone(res);
      toast.success(`Message sent. Ref ${res.ref_code}`);
    } catch (error) {
      toast.error(errMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const close = (value) => {
    onOpenChange(value);
    if (!value) {
      setTimeout(() => {
        setForm({ ...EMPTY, topic: defaultTopic });
        setErrors({});
        setDone(null);
      }, 220);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="enquiry-modal"
        className="max-h-[92vh] overflow-y-auto sm:max-w-lg"
        style={{ background: "var(--gm-navy-925)", borderColor: "rgba(255,255,255,0.14)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Ask Goldmap</DialogTitle>
          <DialogDescription style={{ color: "var(--gm-muted)" }}>
            Drop your question here. We reply on Telegram or email, senang je.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-4 py-2" data-testid="enquiry-success">
            <div
              className="rounded-xl p-4"
              style={{
                background: "rgba(43,213,118,0.08)",
                border: "1px solid rgba(43,213,118,0.28)",
              }}
            >
              <p className="text-sm font-semibold text-white">{done.message}</p>
              <p className="mt-2 text-xs" style={{ color: "var(--gm-muted)" }}>
                Your reference
              </p>
              <p
                className="font-mono-gm text-lg font-semibold"
                data-testid="enquiry-success-code"
                style={{ color: "var(--gm-success)" }}
              >
                {done.ref_code}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                href={config?.telegram_owner || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="gm-btn-red flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold"
                data-testid="enquiry-success-telegram"
              >
                <Send className="h-4 w-4" /> Message on Telegram
              </a>
              <button
                type="button"
                onClick={() => close(false)}
                data-testid="enquiry-close-button"
                className="gm-btn-ghost min-h-[44px] rounded-xl px-4 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form" noValidate>
            <div>
              <Label htmlFor="eq-name" className="text-white">
                Name
              </Label>
              <Input
                id="eq-name"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Amir"
                data-testid="enquiry-name-input"
                className="mt-1.5"
              />
              {errors.name ? (
                <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                  {errors.name}
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="eq-tg" className="text-white">
                  Telegram
                </Label>
                <Input
                  id="eq-tg"
                  value={form.telegram}
                  onChange={set("telegram")}
                  placeholder="@yourhandle"
                  data-testid="enquiry-telegram-input"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="eq-email" className="text-white">
                  Email <span style={{ color: "var(--gm-muted-2)" }}>(optional)</span>
                </Label>
                <Input
                  id="eq-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@email.com"
                  data-testid="enquiry-email-input"
                  className="mt-1.5"
                />
              </div>
            </div>
            {errors.telegram ? (
              <p className="text-xs" style={{ color: "var(--gm-danger)" }}>
                {errors.telegram}
              </p>
            ) : null}

            <div>
              <Label className="text-white">Topic</Label>
              <Select value={form.topic} onValueChange={set("topic")}>
                <SelectTrigger className="mt-1.5" data-testid="enquiry-topic-select">
                  <SelectValue placeholder="Pick a topic" />
                </SelectTrigger>
                <SelectContent>
                  {ENQUIRY_TOPICS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="eq-msg" className="text-white">
                Message
              </Label>
              <Textarea
                id="eq-msg"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you need help with..."
                data-testid="enquiry-message-input"
                className="mt-1.5"
              />
              {errors.message ? (
                <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                  {errors.message}
                </p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="enquiry-submit-button"
              className="gm-btn-red flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {busy ? "Sending..." : "Send message"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
